// Copyright ©️ 2026 eVolve MEP, LLC

import { Fragment, useCallback, useEffect, useState } from 'react';

import {
  type PartAttributeDefinition,
  type PartAttributeDefinitionVariant,
  type UpdateAttributeBody,
  useEvolveDelete,
  useEvolvePaginatedGet,
  useEvolvePut,
} from '@evolvemep/api-ts';
import { Accordion, Button, Card, Divider, Flex, Stack, Text, TextInput, Title, ActionIcon } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { GridMenu } from 'components/Mantine/GridMenu';
import { InlineEditor } from 'components/Mantine/InlineEditor';
import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { useWrappedModal, WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { omit } from 'helpers/omit';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { AddAttributePopover } from 'modules/Shop/CatalogSetup/AddAttributePopover';

const VariantEditor = ({
  attribute: { partAttributeDefinitionId: _, editable, variants, ...attribute },
  variant,
  updateAttribute,
  editing,
  setEditing,
  disabled,
}: {
  attribute: PartAttributeDefinition;
  variant: PartAttributeDefinitionVariant;
  editing: boolean;
  setEditing: (editing: boolean) => void;
  updateAttribute: (attribute: UpdateAttributeBody) => Promise<void>;
  disabled?: boolean;
}) => {
  const [saving, setSaving] = useState(false);

  return (
    <Flex p="sm" pl="md" mih={54} justify="space-between" align="center" key={variant.partAttributeDefinitionVariantId}>
      {editing ? (
        <InlineEditor
          defaultValue={variant.name}
          loading={saving}
          buttonSide="right"
          flexProps={{
            w: '100%',
          }}
          w="100%"
          onSubmit={(newVariantName) => {
            setSaving(true);
            void updateAttribute({
              ...attribute,
              variants: variants.map((v) => {
                if (v.partAttributeDefinitionVariantId !== variant.partAttributeDefinitionVariantId) return v;
                return {
                  ...v,
                  name: newVariantName,
                };
              }),
            })
              .then(() => setEditing(false))
              .finally(() => setSaving(false));
          }}
          onClose={() => setEditing(false)}
        />
      ) : (
        variant.name
      )}
      {!editing && editable && (
        <GridMenu
          disabled={disabled}
          loading={saving}
          options={['Rename', 'Delete']}
          onMenuItemSelected={(item) => {
            if (item === 'Delete') {
              setSaving(true);
              void updateAttribute({
                ...attribute,
                variants: variants.filter(
                  (v) => v.partAttributeDefinitionVariantId !== variant.partAttributeDefinitionVariantId,
                ),
              }).finally(() => setSaving(false));
            }
            if (item === 'Rename') {
              setEditing(true);
            }
          }}
        />
      )}
    </Flex>
  );
};

const AddVariantFields = ({
  attribute,
  updateAttribute,
}: {
  attribute: PartAttributeDefinition;
  updateAttribute: (attribute: UpdateAttributeBody) => Promise<void>;
}) => {
  const form = useForm<{ name: string }>({
    initialValues: { name: '' },
    validate: {
      name: (v) => !v.trim() || attribute.variants.some((variant) => variant.name === v.trim()),
    },
  });

  const onSubmit = async ({ name }: typeof form.values) => {
    await updateAttribute({
      ...omit(attribute, ['partAttributeDefinitionId', 'editable', 'variants']),
      variants: [...attribute.variants, { name: name.trim() }],
    });
  };

  useFormPopover(form, onSubmit);

  return <TextInput label="Variant Name" autoFocus data-autofocus required {...form.getInputProps('name')} />;
};

type EditingState =
  | {
      type: 'attribute';
      attribute: PartAttributeDefinition;
    }
  | {
      type: 'variant';
      attribute: PartAttributeDefinition;
      variant: PartAttributeDefinitionVariant;
    };

const AttributeEditor = ({
  attribute: initialAttribute,
  editingState,
  setEditingName,
  setEditingVariant,
  onDeleteAttribute,
}: {
  attribute: PartAttributeDefinition;
  editingState: EditingState | null;
  setEditingName: (editing: boolean) => void;
  setEditingVariant: (variant: PartAttributeDefinitionVariant | null) => void;
  onDeleteAttribute: () => void;
}) => {
  const [attribute, onAttributeUpdated] = useState(initialAttribute);

  const { apiCall: updateAttributeApiCall, loading: saving } = useEvolvePut(
    `shop/partCatalog/globalAttribute/${attribute.partAttributeDefinitionId}`,
  );

  const updateAttribute = useCallback(
    async (body: UpdateAttributeBody) => updateAttributeApiCall(body).then(onAttributeUpdated),
    [updateAttributeApiCall],
  );
  const [addingVariant, setAddingVariant] = useState(false);

  const editingName = editingState?.type === 'attribute' && editingState.attribute === attribute;
  const editingVariant = editingState?.type === 'variant' && editingState.attribute === attribute;
  useBlockerWhen(`editing-attribute-${initialAttribute.name}`, editingName || editingVariant || addingVariant);

  const [opened, setOpened] = useState<string | null>(null);

  return (
    <Card p={0} withBorder radius="md">
      <Accordion
        value={opened}
        onChange={(v) => {
          if (!editingVariant) setOpened(v);
        }}
        bg={opened ? 'gray.1' : undefined}
        styles={{
          item: {
            border: 'none',
          },
          panel: {
            borderTop: '1px solid var(--mantine-color-gray-3)',
          },
        }}
      >
        <Accordion.Item value={attribute.partAttributeDefinitionId}>
          {/* TODO: This breaks keyboard navigation & the disabled attribute.
              Ideally we want the interactive elements outside the Accordion.Control.
              See https://mantine.dev/core/accordion/#compose-controls `
          */}
          <Accordion.Control component="div" disabled={editingVariant}>
            <Flex justify="space-between" pr="md">
              {editingName ? (
                <InlineEditor
                  defaultValue={attribute.name}
                  onSubmit={(newAttributeName) => {
                    void updateAttribute({
                      ...attribute,
                      name: newAttributeName,
                    }).then(() => setEditingName(false));
                  }}
                  loading={saving}
                  onClose={() => setEditingName(false)}
                  buttonSide="right"
                  flexProps={{
                    w: '100%',
                  }}
                  w="100%"
                />
              ) : (
                <>
                  {attribute.name}
                  <GridMenu
                    disabled={editingState !== null}
                    options={['Rename', 'Delete']}
                    onMenuItemSelected={(menuItemSelected) => {
                      if (menuItemSelected === 'Rename') setEditingName(true);
                      if (menuItemSelected === 'Delete') onDeleteAttribute();
                    }}
                  />
                </>
              )}
            </Flex>
          </Accordion.Control>
          <Accordion.Panel>
            <Flex my="xs" gap="sm" align="center" justify="space-between">
              <Flex align="flex-end" gap="sm">
                <Title order={5}>Variants</Title>
                {attribute.allowWriteInVariant && (
                  <Text c="dimmed" fz="sm">
                    Write-in values allowed
                  </Text>
                )}
              </Flex>
              <FormPopover
                opened={addingVariant}
                onClose={() => setAddingVariant(false)}
                position="bottom-end"
                confirmationText="Add"
                loading={saving}
                target={
                  attribute.editable ? (
                    <Button
                      onClick={() => setAddingVariant(true)}
                      variant="light"
                      size="compact-sm"
                      styles={{
                        section: {
                          'margin-inline-end': 4,
                        },
                      }}
                    >
                      Add variant
                    </Button>
                  ) : undefined
                }
              >
                <AddVariantFields attribute={attribute} updateAttribute={updateAttribute} />
              </FormPopover>
            </Flex>
            <Card p={0} withBorder>
              {attribute.variants.map((variant, i) => (
                <Fragment key={variant.partAttributeDefinitionVariantId}>
                  <VariantEditor
                    attribute={attribute}
                    variant={variant}
                    updateAttribute={updateAttribute}
                    disabled={editingState !== null}
                    editing={editingState?.type === 'variant' && editingState.variant === variant}
                    setEditing={(editing) => setEditingVariant(editing ? variant : null)}
                  />
                  {i < attribute.variants.length - 1 && <Divider />}
                </Fragment>
              ))}
            </Card>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Card>
  );
};

const GlobalAttributeEditorContents = ({ onClose }: { onClose: () => void }) => {
  const [confirmExit, setConfirmExit] = useState(false);
  const { setOnClose } = useWrappedModal();
  const [editingState, setEditingState] = useState<EditingState | null>(null);
  useEffect(() => {
    setOnClose(() => (editingState === null ? onClose : () => setConfirmExit(true)));
  }, [onClose, editingState, setOnClose]);

  const { data, fetchNextPage, loading, reset } = useEvolvePaginatedGet('shop/partCatalog/globalAttribute');

  const { apiCall: deleteAttributeApiCall, loading: deleting } = useEvolveDelete(
    `shop/partCatalog/globalAttribute/:partAttributeDefinitionId`,
  );
  const [deletingAttribute, setDeletingAttribute] = useState<PartAttributeDefinition>();

  return (
    <Stack>
      <Flex justify="flex-end">
        <AddAttributePopover label="New attribute" globalCreator onGlobalAttributeCreated={() => reset()} />
      </Flex>
      {data.map((attribute) => (
        <AttributeEditor
          key={attribute.partAttributeDefinitionId}
          attribute={attribute}
          editingState={editingState}
          setEditingName={(editing) => setEditingState(editing ? { type: 'attribute', attribute } : null)}
          setEditingVariant={(variant) => setEditingState(variant ? { type: 'variant', attribute, variant } : null)}
          onDeleteAttribute={() => setDeletingAttribute(attribute)}
        />
      ))}
      {!loading && data.length === 0 && (
        <Text c="dimmed" fz="sm" ta="center">
          {`Use "New attribute" to add a new global attribute.`}
        </Text>
      )}
      <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />

      <VariableTrackingModal
        title="Delete attribute"
        variable={deletingAttribute}
        onConfirm={async (currentDeletingAttribute) => {
          await deleteAttributeApiCall({
            url: `shop/partCatalog/globalAttribute/${currentDeletingAttribute.partAttributeDefinitionId}`,
          }).then(() => {
            reset();
            notifications.show({
              title: 'Successfully deleted',
              message: `Deleted attribute ${currentDeletingAttribute.name}`,
              color: 'green',
            });
          });
        }}
        onClose={() => setDeletingAttribute(undefined)}
        loading={deleting}
        confirmationText="Delete"
      >
        {(currentDeletingAttribute) => (
          <Text>
            Are you sure you want to delete <b>{currentDeletingAttribute.name}</b>?
          </Text>
        )}
      </VariableTrackingModal>
      <WrappedModal
        title="Unsaved changes"
        opened={confirmExit}
        onClose={() => setConfirmExit(false)}
        onConfirm={() => {
          setConfirmExit(false);
          onClose();
        }}
        confirmationText="Confirm"
      >
        <Text>Your changes have not been saved. Navigating away will remove any unsaved edits.</Text>
      </WrappedModal>
    </Stack>
  );
};

export const GlobalAttributeEditor = ({
  disabled,
  onClose: parentOnClose,
}: {
  disabled?: boolean;
  onClose: () => void;
}) => {
  const [opened, setOpened] = useState(false);
  const onClose = useCallback(() => {
    setOpened(false);
    parentOnClose();
  }, [parentOnClose]);

  return (
    <>
      <AgGridStyleTooltip label="Edit global attributes" withArrow>
        <ActionIcon size="input-sm" onClick={() => setOpened(true)} variant="light" disabled={disabled}>
          <EvolveIcon icon="External" size="md" />
        </ActionIcon>
      </AgGridStyleTooltip>
      <WrappedModal
        title="Global attributes"
        subtitle="Use to filter and apply to parts in your catalogs."
        size="lg"
        opened={opened}
        onClose={onClose}
        hideModalButtons
      >
        <GlobalAttributeEditorContents onClose={onClose} />
      </WrappedModal>
    </>
  );
};
